import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BaseModal from '../../components/Modal/Base/BaseModal';
import { useProductionDailyAPI } from '../../api/production/useProductionDailyAPI';
import { useProductAPI } from '../../api/product/useProductAPI';
import './ProductionDailyInputModal.css';

/**
 * ProductionDailyInputModal — `/production` 생산 실적 입력/수정 모달 (방안 A).
 *
 * - mode="create": 빈 폼(일자=오늘). 제품 select(회사 제품 목록) + 생산/불량 수량 → POST.
 * - mode="edit"  : 주어진 occurDt 의 제품 분해(GET drill-down)를 띄워, 한 건을 골라
 *                  prefill 후 수정(PUT). 등록 버튼과 행 클릭이 *같은 모달* 을 재사용한다.
 *
 * companySeq 는 호출 측(ProductionStatDashboard) 이 session 에서 읽어 prop 으로 내려준다
 * (기존 페이지 패턴 변경 금지). 불량률은 입력받지 않는다(계산값).
 *
 * 사용법: docs/modules/frontend/production/ProductionDailyInputModal.md
 */
const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
};

const ProductionDailyInputModal = ({
  isOpen,
  mode = 'create',
  companySeq,
  occurDt: initialOccurDt,
  onClose,
  onSuccess,
}) => {
  const dailyApi = useProductionDailyAPI();
  const productApi = useProductAPI();

  const [products, setProducts] = useState([]);
  const [breakdown, setBreakdown] = useState([]); // edit 모드 제품 분해
  const [selectedProductSeq, setSelectedProductSeq] = useState(''); // edit 모드에서 고른 행
  const [form, setForm] = useState({
    occurDt: initialOccurDt || todayIso(),
    productSeq: '',
    production: '',
    defect: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === 'edit';

  // 모달 open 시: 회사 제품 목록 + (edit) 일자별 제품 분해 로드. 닫혀 있으면 아무 호출 없음.
  useEffect(() => {
    if (!isOpen || !companySeq) return;
    let cancelled = false;
    (async () => {
      const list = await productApi.product.get({ companySeq });
      if (!cancelled && Array.isArray(list)) setProducts(list);

      if (isEdit && initialOccurDt) {
        const res = await dailyApi.getDaily({ companySeq, occurDt: initialOccurDt });
        if (!cancelled) setBreakdown(res.ok && Array.isArray(res.data) ? res.data : []);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companySeq, isEdit, initialOccurDt]);

  // 모달 재오픈 시 폼 초기화
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSelectedProductSeq('');
    setForm({
      occurDt: initialOccurDt || todayIso(),
      productSeq: '',
      production: '',
      defect: '',
    });
  }, [isOpen, initialOccurDt]);

  // edit 모드: 분해 행 선택 시 폼 prefill. 미매핑(productSeq 없는) 행은 제품 식별 불가 → 선택 차단.
  const handlePickBreakdown = useCallback(
    (row) => {
      if (row.productSeq == null) return; // (미매핑) 행: 수정 불가
      setSelectedProductSeq(String(row.productSeq));
      setForm({
        occurDt: row.occurDt || initialOccurDt,
        productSeq: row.productSeq,
        production: row.production ?? '',
        defect: row.defect ?? '',
      });
      setError('');
    },
    [initialOccurDt],
  );

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const defectRate = useMemo(() => {
    const p = Number(form.production);
    const d = Number(form.defect);
    if (!p || p <= 0) return null;
    return Math.round((d / p) * 10000) / 100;
  }, [form.production, form.defect]);

  // 클라이언트 검증 — 음수 금지, 불량 ≤ 생산.
  const validate = () => {
    if (!form.occurDt) return '일자를 선택하세요.';
    if (!form.productSeq) return '제품을 선택하세요.';
    const production = Number(form.production);
    const defect = Number(form.defect);
    if (form.production === '' || Number.isNaN(production) || production < 0)
      return '생산수량은 0 이상이어야 합니다.';
    if (form.defect === '' || Number.isNaN(defect) || defect < 0)
      return '불량수량은 0 이상이어야 합니다.';
    if (defect > production) return '불량수량은 생산수량을 넘을 수 없습니다.';
    return '';
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = {
      companySeq,
      occurDt: form.occurDt,
      productSeq: Number(form.productSeq),
      production: Number(form.production),
      defect: Number(form.defect),
    };
    const res = isEdit
      ? await dailyApi.updateDaily(payload)
      : await dailyApi.createDaily(payload);
    setSubmitting(false);
    if (res.ok) {
      if (typeof onSuccess === 'function') onSuccess();
      if (typeof onClose === 'function') onClose();
    } else {
      setError(res.message || '요청 처리 중 오류가 발생했습니다.');
    }
  };

  if (!isOpen) return null;

  const title = isEdit ? '생산 실적 수정' : '생산 등록';
  const canEditForm = !isEdit || selectedProductSeq !== '';

  return (
    <BaseModal isOpen={isOpen} title={title} onClose={onClose}>
      <div className="prod-daily-modal">
        {isEdit && (
          <div className="prod-daily-breakdown">
            <div className="prod-daily-breakdown__head">
              {initialOccurDt} 제품별 생산 실적
            </div>
            {breakdown.length === 0 ? (
              <div className="prod-daily-breakdown__empty">
                해당 일자에 입력된 제품 실적이 없습니다.
              </div>
            ) : (
              <ul className="prod-daily-breakdown__list">
                {breakdown.map((row, idx) => {
                  // 출처 무관 전수 표시. contract 매핑 실패 행은 productSeq=null → (미매핑) 비클릭.
                  const isMapped = row.productSeq != null;
                  return (
                    <li
                      key={isMapped ? row.productSeq : `unmapped-${idx}`}
                      className={
                        'prod-daily-breakdown__item' +
                        (isMapped && String(row.productSeq) === selectedProductSeq
                          ? ' is-selected'
                          : '') +
                        (isMapped ? '' : ' is-unmapped')
                      }
                      role={isMapped ? 'button' : undefined}
                      tabIndex={isMapped ? 0 : undefined}
                      onClick={isMapped ? () => handlePickBreakdown(row) : undefined}
                      onKeyDown={
                        isMapped
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ')
                                handlePickBreakdown(row);
                            }
                          : undefined
                      }
                    >
                      <span className="prod-daily-breakdown__name">
                        {isMapped ? (row.productName ?? '미상') : '(미매핑)'}
                      </span>
                      <span className="prod-daily-breakdown__nums">
                        생산 {(Number(row.production) || 0).toLocaleString()} / 불량{' '}
                        {(Number(row.defect) || 0).toLocaleString()}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="prod-daily-form">
          <label className="prod-daily-field">
            <span>일자</span>
            <input
              type="date"
              value={form.occurDt}
              onChange={handleChange('occurDt')}
              disabled={isEdit}
            />
          </label>

          <label className="prod-daily-field">
            <span>제품</span>
            <select
              value={form.productSeq}
              onChange={handleChange('productSeq')}
              disabled={isEdit}
            >
              <option value="">제품 선택</option>
              {products.map((p) => (
                <option key={p.productSeq} value={p.productSeq}>
                  {p.productName}
                </option>
              ))}
            </select>
          </label>

          <label className="prod-daily-field">
            <span>생산수량</span>
            <input
              type="number"
              min="0"
              value={form.production}
              onChange={handleChange('production')}
              disabled={!canEditForm}
            />
          </label>

          <label className="prod-daily-field">
            <span>불량수량</span>
            <input
              type="number"
              min="0"
              value={form.defect}
              onChange={handleChange('defect')}
              disabled={!canEditForm}
            />
          </label>

          <div className="prod-daily-field prod-daily-field--readonly">
            <span>불량률</span>
            <span className="prod-daily-defect-rate">
              {defectRate === null ? '—' : `${defectRate}%`}
            </span>
          </div>
        </div>

        {error && <div className="prod-daily-error">{error}</div>}

        <div className="prod-daily-actions">
          <button
            type="button"
            className="prod-daily-btn prod-daily-btn--cancel"
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className="prod-daily-btn prod-daily-btn--submit"
            onClick={handleSubmit}
            disabled={submitting || !canEditForm}
          >
            {isEdit ? '수정' : '등록'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

export default ProductionDailyInputModal;
